import { useRef, useState } from 'react';
import { IconLock, IconPlus, IconX } from '@tabler/icons-react';
import { toBnDigits } from '../../../../../utils/bnFormat';

/**
 * The page strip: every page in the job, the one being edited ringed, an add
 * tile at the top and a delete cross on each.
 *
 * Drag to reorder is plain HTML5 drag-and-drop rather than dnd-kit. The rail is
 * a single list of small tiles with no keyboard-sortable requirement, and the
 * native API costs nothing to load — dnd-kit earns its keep in the PDF page grid
 * where the items are large and dropped between columns.
 */
export default function DocumentCleanupPageRail({
    pages,
    selectedId,
    onSelect,
    onRemove,
    onReorder,
    onAdd,
    disabled = false,
    labels,
}) {
    // The dragged id lives in a ref as well as state: `drop` can arrive in the
    // same tick as `dragstart` (touch shims, synthetic events), and a state
    // update has not landed by then — the ref has. State is only for the dimming.
    const dragRef = useRef(null);
    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);
    const railRef = useRef(null);

    return (
        <div
            ref={railRef}
            className="flex h-full min-h-0 w-full flex-col gap-2 overflow-y-auto overflow-x-hidden pr-0.5"
        >
            <button
                type="button"
                onClick={onAdd}
                disabled={disabled}
                title={labels.add}
                aria-label={labels.add}
                className="flex aspect-[3/4] w-full flex-none items-center justify-center rounded-xl border-2 border-dashed border-[#D9D4CE] bg-white text-[#A8A099] transition-colors hover:border-amber-400 hover:text-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-500"
            >
                <IconPlus size={26} stroke={1.8} />
            </button>

            {pages.map((page, index) => {
                const active = page.id === selectedId;
                return (
                    <div
                        key={page.id}
                        draggable={!disabled}
                        onDragStart={() => { dragRef.current = page.id; setDragId(page.id); }}
                        onDragEnd={() => { dragRef.current = null; setDragId(null); setOverId(null); }}
                        onDragOver={(e) => { e.preventDefault(); setOverId(page.id); }}
                        onDrop={(e) => {
                            e.preventDefault();
                            const from = dragRef.current;
                            if (from && from !== page.id) onReorder(from, page.id);
                            dragRef.current = null;
                            setDragId(null);
                            setOverId(null);
                        }}
                        className={`group relative aspect-[3/4] w-full flex-none overflow-hidden rounded-xl border-2 bg-white transition-all dark:bg-zinc-900 ${
                            active
                                ? 'border-amber-500 shadow-[0_2px_10px_rgba(244,121,26,0.25)]'
                                : 'border-[#ECE8E4] hover:border-amber-300 dark:border-zinc-700'
                        } ${overId === page.id && dragId !== page.id ? 'ring-2 ring-amber-400' : ''} ${
                            dragId === page.id ? 'opacity-50' : ''
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => onSelect(page.id)}
                            className="block h-full w-full cursor-pointer"
                            title={page.name}
                            aria-label={`${labels.page} ${index + 1}`}
                            aria-pressed={active}
                        >
                            {page.thumbUrl ? (
                                // `thumbEditedUrl` (Document Print only): the same thumbnail with the page's brightness / contrast / B&W applied
                                <img src={page.thumbEditedUrl || page.thumbUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                                <span className="flex h-full w-full items-center justify-center text-[11px] text-[#A8A099] dark:text-zinc-500">
                                    {labels.loading}
                                </span>
                            )}
                        </button>

                        <span className="pointer-events-none absolute bottom-1 left-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
                            {toBnDigits(index + 1)}
                        </span>

                        {/* A customer's private file (Document Print): it is deleted from the booth once printed / downloaded */}
                        {page.booth?.isPrivate && labels.private ? (
                            <span role="img" title={labels.private} aria-label={labels.private} className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white shadow">
                                <IconLock size={12} stroke={2.4} />
                            </span>
                        ) : null}

                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRemove(page.id); }}
                            disabled={disabled}
                            title={labels.remove}
                            aria-label={labels.remove}
                            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#E5484D] text-white opacity-0 shadow transition-opacity hover:bg-[#cf3f44] focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed"
                        >
                            <IconX size={14} stroke={2.4} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
